import React from 'react';
import ReactMarkdown from 'markdown-to-jsx';
import BEMHelper from '@ynap/bem-helper';

export interface CoreMediaContent {
    deliveryText?: string;
    returnsText?: string;
    loading?: boolean;
    error?: boolean;
}

export interface DeliveryAndReturnsLegalProps {
    className?: string;
    coreMediaMessage: CoreMediaContent;
    locale: string;
    config: Config;
    messages: {
        accordionTitles: {
            deliveryAndReturns: () => React.ReactNode;
        };
    };
    setModal?: (modalType: string) => void;
}

const bem = new BEMHelper('DeliveryAndReturnsLegal');

export const createDeliveryReturnsUrls = (locale: string, isOutnetDeliveryReturnsLink: boolean) => {
    const basePath = isOutnetDeliveryReturnsLink ? 'page' : 'content';

    return {
        deliveryLink: `/${locale}/${basePath}/help/delivery`,
        returnsLink: `/${locale}/${basePath}/help/${isOutnetDeliveryReturnsLink ? 'returns' : 'exchanges-and-returns'}`,
    };
};

const sanitizeText = (text?: string): string => {
    return text && typeof text === 'string' && text.trim() ? text : '';
};

const DeliveryAndReturnsLegal = ({ className, coreMediaMessage = {}, locale, config, messages, setModal }: DeliveryAndReturnsLegalProps) => {
    const isOutnetDeliveryReturnsLink = !!config?.head?.pdp?.deliveryAndReturnsLink;
    const { deliveryText, returnsText, error } = coreMediaMessage;
    const { deliveryLink, returnsLink } = createDeliveryReturnsUrls(locale, isOutnetDeliveryReturnsLink);

    const sanitizedDeliveryText = sanitizeText(deliveryText);
    const sanitizedReturnsText = sanitizeText(returnsText);

    if (error || !sanitizedDeliveryText || !sanitizedReturnsText) {
        return null;
    }

    const createLinkOverride =
        (href: string, useModal: boolean) =>
        ({ children }) => (
            <a
                href={useModal ? '#' : href}
                onClick={
                    useModal
                        ? (e) => {
                              e.preventDefault();
                              setModal?.('deliveryAndReturns');
                          }
                        : undefined
                }
                className={bem('link')}
            >
                {children}
            </a>
        );

    const deliveryLinkOverride = createLinkOverride(deliveryLink, isOutnetDeliveryReturnsLink);
    const returnsLinkOverride = createLinkOverride(returnsLink, false);

    return (
        <div className={bem(null, null, className)}>
            {isOutnetDeliveryReturnsLink && <div className={bem('title')}>{messages?.accordionTitles?.deliveryAndReturns()}</div>}
            <div className={bem('deliveryAndReturnsWrapper')}>
                <div className={bem('deliveryIcon')} />
                <ReactMarkdown
                    className={bem('text')}
                    options={{
                        wrapper: 'div',
                        overrides: {
                            p: ({ ...props }) => <span className={bem('text')}>{props.children}</span>,
                            a: deliveryLinkOverride,
                        },
                    }}
                >
                    {sanitizedDeliveryText}
                </ReactMarkdown>
            </div>
            <div className={bem('deliveryAndReturnsWrapper')}>
                <div className={bem('returnsIcon')} />
                <ReactMarkdown
                    className={bem('text')}
                    options={{
                        wrapper: 'div',
                        overrides: {
                            p: ({ ...props }) => <span className={bem('text')}>{props.children}</span>,
                            a: returnsLinkOverride,
                        },
                    }}
                >
                    {sanitizedReturnsText}
                </ReactMarkdown>
            </div>
        </div>
    );
};

export default DeliveryAndReturnsLegal;
